import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { PublicHeader } from '@/components/PublicHeader'
import { PublicFooter } from '@/components/PublicFooter'
import { useParentAuth } from '@/hooks/useParentAuth'
import { sendOtpMutation, verifyOtpMutation } from '@/lib/client/@tanstack/react-query.gen'

export const Route = createFileRoute('/verify')({
  component: VerifyOtpPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      email: (search.email as string) || '',
    }
  },
})

function VerifyOtpPage() {
  const navigate = useNavigate()
  const { email } = Route.useSearch()
  const { setToken } = useParentAuth()

  const maskedEmail = email.replace(/(.{1})(.*)(@.*)/, '$1***$3')

  const verifyOtp = useMutation({
    ...verifyOtpMutation({}),
    onSuccess: (data) => {
      if (data && data.token) {
        setToken(data.token)
        toast.success('Connexion réussie')
        navigate({ to: '/link/$parentId', params: { parentId: data.parentId! } })
      }
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const resendOtp = useMutation({
    ...sendOtpMutation({}),
    onSuccess: () => {
      toast.success('Un nouveau code a été envoyé')
    },
    onError: () => {
      toast.error('Erreur lors de l\'envoi du code')
    },
  })

  const form = useForm({
    defaultValues: {
      code: '',
    },
    onSubmit: async ({ value }) => {
      verifyOtp.mutate({
        body: { email, code: value.code },
      })
    },
  })

  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Vérification du code</CardTitle>
            <CardDescription>
              Code envoyé à {maskedEmail}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                form.handleSubmit()
              }}
              className="space-y-4"
            >
              <form.Field
                name="code"
                validators={{
                  onChange: ({ value }) => {
                    const result = z.string().regex(/^\d{6}$/, 'Le code doit contenir 6 chiffres').safeParse(value)
                    return result.success ? undefined : result.error.issues[0]?.message
                  },
                }}
              >
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="code">Code à 6 chiffres</Label>
                    <Input
                      id="code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="123456"
                      value={field.state.value}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '')
                        field.handleChange(value)
                      }}
                      onBlur={field.handleBlur}
                      className="text-center text-2xl tracking-widest font-mono"
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-red-500">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              <Button
                type="submit"
                className="w-full"
                disabled={verifyOtp.isPending}
              >
                {verifyOtp.isPending ? 'Vérification...' : 'Vérifier'}
              </Button>

              <Button
                type="button"
                variant="outline" 
                className="w-full"
                disabled={resendOtp.isPending}
                onClick={() => resendOtp.mutate({ body: { email } })}
              >
                {resendOtp.isPending ? 'Envoi...' : 'Renvoyer le code'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
      <PublicFooter />
    </div>
  )
}
